import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AppIcon } from '@app/shared/controls/app-icon/app-icon';
import { NgSelectModule, NgLabelTemplateDirective, NgOptionTemplateDirective } from '@ng-select/ng-select';
import { TranslateService } from '@ngx-translate/core';

type DropdownPosition = 'auto' | 'top' | 'bottom';
type DropdownIconPath = string | ((item: unknown) => string | null | undefined) | null;

@Component({
  selector: 'app-dropdown',
  imports: [AppIcon, FormsModule, NgSelectModule, NgLabelTemplateDirective, NgOptionTemplateDirective],
  templateUrl: './app-dropdown.html',
  styleUrl: './app-dropdown.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DropdownComponent,
      multi: true,
    },
  ],
})
export class DropdownComponent implements ControlValueAccessor {
  private readonly translate = inject(TranslateService);

  readonly items = input<readonly unknown[]>([]);
  readonly bindLabel = input<string>('');
  readonly bindValue = input<string>('');
  readonly labelIconPath = input<string | null>(null);
  readonly labelIconColor = input<string | null>(null);
  readonly itemIconPath = input<DropdownIconPath>(null);
  readonly itemIconColor = input<string | null>(null);
  readonly labelFontWeight = input<number | string>(400);
  readonly labelColor = input<string>('var(--safegpt-text)');
  readonly labelFontSize = input<number | string>('var(--safegpt-font-size-sm)');
  readonly templateLabel = input<((item: unknown) => string) | null>(null);
  readonly templateOption = input<((item: unknown) => string) | null>(null);
  readonly dropdownMenuWidth = input<string | null>(null);
  readonly dropdownPosition = input<DropdownPosition>('auto');
  readonly placeholder = input<string>('');
  readonly nullText = input<string>('');
  readonly clearable = input(true);
  readonly searchable = input(true);
  readonly multiple = input(false);
  readonly loading = input(false);
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly searchFn = input<((term: string, item: unknown) => boolean) | null>(null);
  readonly notFoundText = input(this.translate.instant('shared.dropdown.noItemsFound'));
  readonly labelForId = input<string | null>(null);
  readonly borderColor = input<string | null>('var(--safegpt-border-strong)');
  readonly hoverBackgroundColor = input<string | null>(null);
  readonly hoverArrowColor = input<string | null>(null);
  readonly optionHoverBackgroundColor = input<string | null>(null);
  readonly showArrowOnHover = input(false);
  readonly searchChanged = output<string>();

  protected readonly value = signal<unknown>(null);
  protected readonly disabledByForm = signal(false);
  protected readonly disabledState = computed(() => this.disabled() || this.disabledByForm());
  protected readonly displayPlaceholder = computed(() => this.nullText() || this.placeholder());
  protected readonly hasSelection = computed(() => {
    const value = this.value();
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value !== null && value !== undefined && value !== '';
  });

  private onChange: (value: unknown) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: unknown): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm.set(isDisabled);
  }

  protected handleModelChange(value: unknown): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  protected handleBlur(): void {
    this.onTouched();
  }

  protected handleSearch(event: { term: string }): void {
    this.searchChanged.emit(event.term);
  }

  protected hasCustomMenuWidth(): boolean {
    return !!this.dropdownMenuWidth();
  }

  protected hasLabelIcon(): boolean {
    return !!this.labelIconPath();
  }

  protected hasNullText(): boolean {
    return !!this.nullText();
  }

  protected hasItemIcon(): boolean {
    return !!this.itemIconPath();
  }

  protected hasBorder(): boolean {
    return !!this.borderColor();
  }

  protected hasHoverBackgroundColor(): boolean {
    return !!this.hoverBackgroundColor();
  }

  protected hasHoverArrowColor(): boolean {
    return !!this.hoverArrowColor();
  }

  protected hasOptionHoverBackgroundColor(): boolean {
    return !!this.optionHoverBackgroundColor();
  }

  protected renderTemplateLabel(item: unknown): string {
    const renderer = this.templateLabel();
    return renderer ? renderer(item) : '';
  }

  protected renderLabel(item: unknown): string {
    const renderer = this.templateLabel();
    if (renderer) {
      return renderer(item);
    }

    const labelKey = this.bindLabel();
    if (labelKey && this.isRecord(item)) {
      const value = item[labelKey];
      return this.toDisplayText(value);
    }

    return this.toDisplayText(item);
  }

  protected renderTemplateOption(item: unknown): string {
    const renderer = this.templateOption();
    return renderer ? renderer(item) : '';
  }

  protected resolveItemIconPath(item: unknown): string | null {
    const icon = this.itemIconPath();
    if (!icon) {
      return null;
    }

    if (typeof icon === 'function') {
      return icon(item) ?? null;
    }

    return icon;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private toDisplayText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (
      typeof value === 'string'
      || typeof value === 'number'
      || typeof value === 'boolean'
      || typeof value === 'bigint'
    ) {
      return `${value}`;
    }

    return '';
  }
}
